import React, { useState } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Container,
  useMediaQuery,
  ListItem,
  ListItemText,
  Drawer,
  List,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import PolicyIcon from "@mui/icons-material/Policy";
import MenuIcon from "@mui/icons-material/Menu";
import LanguageIcon from "@mui/icons-material/Language";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";

// Assets
import DynamicDeskLogo from "../assets/DD_2_Weiss.png";

// Header and Footer Components
import AboutUs from "../headers/AboutUs";
import HelpFAQPage from "../headers/HelpFAQPage";
import PrivacyPolicyPage from "../footers/PrivacyPolicyPage";
import TermsPage from "../footers/TermsPage";
import CookiePolicyPage from "../footers/CookiePolicyPage";
import ImprintPage from "../footers/ImprintPage";
import AdditionalTermsPage from "../footers/AdditionalTermsPage";

// Product-specific components
import ProductEndCustomer from "../headers/corporate/ProductEndCustomer";
import ProductParticipantPage from "../headers/preventive_course/ProductParticipantPage";
import ProductPhysioCustomer from "../headers/physio/ProductPhysioCustomer";

import { useProductContext } from "../hooks/useProductContext";
import FeedbackComponent from "../feedback/FeedbackComponent";

/**
 * AuthPageWrapper Component
 *
 * Wrapper component that provides header, footer, and navigation functionality
 * for authentication pages (SignIn, SignUp)
 */
const AuthPageWrapper = ({ children, isMobile = false }) => {
  const { t, i18n } = useTranslation();
  const productContext = useProductContext();

  // State for menu management
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [footerMenuAnchor, setFooterMenuAnchor] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const isMobileView = useMediaQuery("(max-width:700px)");

  // State for showing different pages
  const [currentPage, setCurrentPage] = useState("auth");

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    setLanguageMenuAnchor(null);
  };

  const renderCurrentPage = () => {
    switch (currentPage) {
      case "about":
        return (
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={() => setCurrentPage("auth")}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 1000,
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            <AboutUs />
          </Box>
        );
      case "help":
        return (
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={() => setCurrentPage("auth")}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 1000,
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            <HelpFAQPage />
          </Box>
        );
      case "moreInfo":
        return (
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={() => setCurrentPage("auth")}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 1000,
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            {productContext === "corporate" ? (
              <ProductEndCustomer />
            ) : productContext === "preventive_course" ? (
              <ProductParticipantPage />
            ) : productContext === "physio_therapist" ? (
              <ProductPhysioCustomer />
            ) : (
              <ProductEndCustomer />
            )}
          </Box>
        );
      case "feedback":
        return (
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={() => setCurrentPage("auth")}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 1000,
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            <FeedbackComponent />
          </Box>
        );

      case "privacy":
        return (
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={() => setCurrentPage("auth")}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 1000,
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            <PrivacyPolicyPage />
          </Box>
        );
      case "terms":
        return (
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={() => setCurrentPage("auth")}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 1000,
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            <TermsPage />
          </Box>
        );
      case "cookies":
        return (
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={() => setCurrentPage("auth")}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 1000,
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            <CookiePolicyPage />
          </Box>
        );
      case "imprint":
        return (
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={() => setCurrentPage("auth")}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 1000,
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            <ImprintPage />
          </Box>
        );
      case "additional-terms":
        return (
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={() => setCurrentPage("auth")}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 1000,
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            <AdditionalTermsPage />
          </Box>
        );
      default:
        return children;
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Drawer
        anchor="right"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            backgroundColor: "#00003e",
            color: "white",
            paddingTop: "80px",
          },
        }}
      >
        <List>
          {/* Back button */}
          {currentPage !== "auth" && (
            <ListItem
              button
              onClick={() => {
                setCurrentPage("auth");
                setMobileDrawerOpen(false);
              }}
            >
              <ArrowBackIcon />
            </ListItem>
          )}

          <ListItem
            button
            onClick={() => {
              setCurrentPage("about");
              setMobileDrawerOpen(false);
            }}
          >
            <ListItemText primary={t("landingPage.aboutUs")} />
          </ListItem>

          <ListItem
            button
            onClick={() => {
              setCurrentPage("help");
              setMobileDrawerOpen(false);
            }}
          >
            <ListItemText primary={t("landingPage.helpFaq")} />
          </ListItem>

          <ListItem
            button
            onClick={() => {
              setCurrentPage("moreInfo");
              setMobileDrawerOpen(false);
            }}
          >
            <ListItemText primary={t("landingPage.moreInfo")} />
          </ListItem>

          <ListItem
            button
            onClick={() => {
              setCurrentPage("feedback");
              setMobileDrawerOpen(false);
            }}
          >
            <ListItemText primary={t("feedback")} />
          </ListItem>

          {/* Language */}
          <ListItem
            button
            onClick={(e) => {
              setLanguageMenuAnchor(e.currentTarget);
            }}
          >
            <ListItemText primary={t("landingPage.language")} />
          </ListItem>

          {/* Policies menu */}
          <ListItem
            button
            onClick={(e) => {
              setFooterMenuAnchor(e.currentTarget);
            }}
          >
            <ListItemText primary={t("landingPage.legal")} />
          </ListItem>
        </List>
      </Drawer>

      {/* Header */}
      <AppBar position="fixed" sx={{ backgroundColor: "#00003e" }}>
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "15px",
          }}
        >
          {/* Logo */}
          <a href={process.env.REACT_APP_FIREBASE_BASE_URL}>
            <img
              src={DynamicDeskLogo}
              style={{ width: "auto", height: "50px" }}
              alt="Dynamic Desk Logo"
            />
          </a>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            {isMobileView ? (
              <>
                {/* Mobile: Burger menu */}
                <IconButton
                  color="inherit"
                  onClick={() => setMobileDrawerOpen(true)}
                  size="large"
                >
                  <MenuIcon />
                </IconButton>
              </>
            ) : (
              <>
                {/* Header Navigation */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {currentPage !== "auth" && (
                    <IconButton
                      color="inherit"
                      onClick={() => setCurrentPage("auth")}
                      size="small"
                    >
                      <ArrowBackIcon />
                    </IconButton>
                  )}

                  <Typography
                    variant="body2"
                    onClick={() => setCurrentPage("about")}
                    sx={{
                      color: "white",
                      cursor: "pointer",
                      px: 1,
                      whiteSpace: "nowrap",
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    {t("landingPage.aboutUs")}
                  </Typography>

                  <Typography
                    variant="body2"
                    onClick={() => setCurrentPage("help")}
                    sx={{
                      color: "white",
                      cursor: "pointer",
                      px: 1,
                      whiteSpace: "nowrap",
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    {t("landingPage.helpFaq")}
                  </Typography>

                  <Typography
                    variant="body2"
                    onClick={() => setCurrentPage("moreInfo")}
                    sx={{
                      color: "white",
                      cursor: "pointer",
                      px: 1,
                      whiteSpace: "nowrap",
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    {t("landingPage.moreInfo")}
                  </Typography>
                  <Typography
                    variant="body2"
                    onClick={() => setCurrentPage("feedback")}
                    sx={{
                      color: "white",
                      cursor: "pointer",
                      px: 1,
                      whiteSpace: "nowrap",
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    {t("feedback")}
                  </Typography>
                </Box>

                <IconButton
                  color="inherit"
                  onClick={(e) => setLanguageMenuAnchor(e.currentTarget)}
                >
                  <LanguageIcon />
                </IconButton>

                <IconButton
                  color="inherit"
                  onClick={(e) => setFooterMenuAnchor(e.currentTarget)}
                >
                  <PolicyIcon />
                </IconButton>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Language Menu */}
      <Menu
        anchorEl={languageMenuAnchor}
        open={Boolean(languageMenuAnchor)}
        onClose={() => setLanguageMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleLanguageChange("en")}>
          {t("landingPage.english")}
        </MenuItem>
        <MenuItem onClick={() => handleLanguageChange("de")}>
          {t("landingPage.german")}
        </MenuItem>
      </Menu>

      {/* Footer Menu */}
      <Menu
        anchorEl={footerMenuAnchor}
        open={Boolean(footerMenuAnchor)}
        onClose={() => setFooterMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setCurrentPage("privacy");
            setFooterMenuAnchor(null);
          }}
        >
          {t("landingPage.privacyPolicy")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setCurrentPage("terms");
            setFooterMenuAnchor(null);
          }}
        >
          {t("landingPage.termsOfService")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setCurrentPage("cookies");
            setFooterMenuAnchor(null);
          }}
        >
          {t("landingPage.cookiePolicy")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setCurrentPage("imprint");
            setFooterMenuAnchor(null);
          }}
        >
          {t("landingPage.imprint")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setCurrentPage("additional-terms");
            setFooterMenuAnchor(null);
          }}
        >
          {t("landingPage.additionalTerms")}
        </MenuItem>
      </Menu>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, marginTop: 10 }}>
        {renderCurrentPage()}
      </Box>

      {/* Footer */}
      {currentPage === "auth" && (
        <Box
          component="footer"
          sx={{
            py: 3,
            px: 2,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 3,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="body2"
                onClick={() => setCurrentPage("privacy")}
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  cursor: "pointer",
                  "&:hover": { color: "white" },
                }}
              >
                {t("landingPage.privacyPolicy")}
              </Typography>

              <Typography
                variant="body2"
                onClick={() => setCurrentPage("terms")}
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  cursor: "pointer",
                  "&:hover": { color: "white" },
                }}
              >
                {t("landingPage.termsOfService")}
              </Typography>

              <Typography
                variant="body2"
                onClick={() => setCurrentPage("cookies")}
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  cursor: "pointer",
                  "&:hover": { color: "white" },
                }}
              >
                {t("landingPage.cookiePolicy")}
              </Typography>

              <Typography
                variant="body2"
                onClick={() => setCurrentPage("imprint")}
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  cursor: "pointer",
                  "&:hover": { color: "white" },
                }}
              >
                {t("landingPage.imprint")}
              </Typography>

              <Typography
                variant="body2"
                onClick={() => setCurrentPage("additional-terms")}
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  cursor: "pointer",
                  "&:hover": { color: "white" },
                }}
              >
                {t("landingPage.additionalTerms")}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              align="center"
              sx={{ color: "rgba(255, 255, 255, 0.5)", mt: 2 }}
            >
              © 2025 Desk Dynamics. {t("landingPage.allRightsReserved")}
            </Typography>
          </Container>
        </Box>
      )}
    </Box>
  );
};

export default AuthPageWrapper;
